import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ContactSEO } from "../components/SEO";
import api from "../lib/api";
import useFooterAddress from "../lib/useFooterAddress";
import { openWhatsAppEnquiry } from "../lib/whatsappNotify";

// Pretty labels for the ?package=<tier> query param used by the Packages page CTAs.
const PACKAGE_LABELS = {
  bronze: "Bronze Package (Tenant Find Only)",
  silver: "Silver Package (Rent Collection)",
  gold: "Gold Package (Fully Managed)",
  platinum: "Platinum Package (Investor & HMO Management)",
};

// Pretty labels for the ?source=<slug> query param used by the sticky valuation CTA.
const SOURCE_LABELS = {
  "landlords-page": "Landlords page (sticky CTA)",
  "property-management-page": "Property Management page (sticky CTA)",
  "lettings-page": "Lettings page (sticky CTA)",
};

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const packageParam = (searchParams.get("package") || "").toLowerCase();
  const packageLabel = PACKAGE_LABELS[packageParam];
  const sourceParam = (searchParams.get("source") || "").toLowerCase();
  const sourceLabel = SOURCE_LABELS[sourceParam];
  const { mapEmbedUrl, mapLinkUrl, address } = useFooterAddress();

  // Compose the initial prefill: package CTA takes priority over sticky-CTA source.
  const initialMessage = packageLabel
    ? `Subject: Enquiry about ${packageLabel}\n\nHi Keystone team,\n\nI'd like to learn more about the ${packageLabel}. Please get in touch to discuss next steps.\n\nThanks,`
    : sourceLabel
    ? `Subject: Free Valuation Request\n\nHi Keystone team,\n\nI'd like to request a free valuation for my property. Please get in touch to arrange a time.\n\n(Source: ${sourceLabel})\n\nThanks,`
    : "";

  // The slug we send back to the API for the Source column in admin.
  // Package CTAs win over sticky-CTA source for attribution.
  const initialSourceSlug = packageParam
    ? `package-${packageParam}`
    : sourceParam || "";

  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: initialMessage,
    submission_type: packageLabel || sourceLabel ? "valuation" : "general",
    source: initialSourceSlug,
    send_copy: false,
  });

  // If the user arrives later with a different ?package or ?source query (e.g. SPA nav
  // between CTAs), refresh the prefill — but only when the message is still empty or
  // matches the previous prefill, so we never overwrite real input.
  useEffect(() => {
    if (!packageLabel && !sourceLabel) return;
    setFormData((prev) => {
      const looksLikePrefill =
        !prev.message ||
        prev.message.startsWith("Subject: Enquiry about ") ||
        prev.message.startsWith("Subject: Free Valuation Request");
      if (!looksLikePrefill) return prev;
      const nextMessage = packageLabel
        ? `Subject: Enquiry about ${packageLabel}\n\nHi Keystone team,\n\nI'd like to learn more about the ${packageLabel}. Please get in touch to discuss next steps.\n\nThanks,`
        : `Subject: Free Valuation Request\n\nHi Keystone team,\n\nI'd like to request a free valuation for my property. Please get in touch to arrange a time.\n\n(Source: ${sourceLabel})\n\nThanks,`;
      const nextSource = packageParam ? `package-${packageParam}` : sourceParam || prev.source;
      return { ...prev, submission_type: "valuation", message: nextMessage, source: nextSource };
    });
  }, [packageLabel, sourceLabel, packageParam, sourceParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitContact(formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      // Fire the full WhatsApp notification — includes name, email, phone,
      // subject/source and the complete message body (split into parts
      // automatically if it exceeds the safe URL length).
      openWhatsAppEnquiry({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        submission_type: formData.submission_type,
        source: formData.source,
        subject: packageLabel || sourceLabel || null,
        body: formData.message,
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        submission_type: "general",
        source: "",
        send_copy: false,
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "01924 520163",
      link: "tel:01924 520163",
    },
    {
      icon: MapPin,
      title: "Address",
      content: "17 Commercial Street, Batley, WF17 5HJ",
      link: null,
    },
    {
      icon: Clock,
      title: "Service Area",
      content: "West Yorkshire — Leeds, Bradford, Batley, Dewsbury",
      link: null,
    },
  ];

  return (
    <div data-testid="contact-page">
      <ContactSEO />
      {/* Hero Section */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              Contact Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              Contact Keystone Properties
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Visit our Batley office or get in touch by phone or message.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((item, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-navy" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-navy mb-1">{item.title}</div>
                    {item.link ? (
                      <a href={item.link} className="text-slate-600 hover:text-gold transition-colors">
                        {item.content}
                      </a>
                    ) : (
                      <div className="text-slate-600">{item.content}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
                {packageLabel ? `Enquire About the ${packageLabel.split(" Package")[0]} Package` : "Get in Touch Today"}
              </h2>
              <p className="text-slate-600 mb-8">
                {packageLabel
                  ? `We've pre-filled your message with your package selection — feel free to add any specific questions before sending.`
                  : `Fill out the form below and we'll get back to you as soon as possible.`}
              </p>

              {packageLabel && (
                <div
                  className="mb-6 rounded-lg border border-gold/30 bg-gold/5 px-4 py-3 flex items-start gap-3"
                  data-testid="contact-package-banner"
                >
                  <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-gold text-sm font-bold">★</span>
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-navy">Selected package</div>
                    <div className="text-slate-600">{packageLabel}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Your Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Email Address *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="contact-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Enquiry Type</label>
                  <Select 
                    value={formData.submission_type} 
                    onValueChange={(v) => setFormData({ ...formData, submission_type: v })}
                  >
                    <SelectTrigger data-testid="contact-type">
                      <SelectValue placeholder="Select enquiry type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Enquiry</SelectItem>
                      <SelectItem value="valuation">Free Rental Valuation</SelectItem>
                      <SelectItem value="property_enquiry">Property Enquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Your Message *</label>
                  <Textarea
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
                  <Checkbox
                    id="contact-send-copy"
                    checked={formData.send_copy}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, send_copy: checked === true })
                    }
                    data-testid="contact-send-copy"
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="contact-send-copy"
                    className="text-sm text-slate-700 leading-snug cursor-pointer select-none"
                  >
                    Also send me a copy of this message
                    <span className="block text-xs text-slate-500 mt-0.5">
                      We'll email you a confirmation with the exact text you submitted.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light"
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>

            {/* Map */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
                Find Us
              </h2>
              <p className="text-slate-600 mb-8">
                Our office is located in the heart of Batley, easily accessible from across West Yorkshire.
              </p>
              <div className="h-96 rounded-lg overflow-hidden bg-slate-200 shadow-lg">
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Keystone Properties Location - ${address}`}
                  data-testid="contact-map-iframe"
                ></iframe>
              </div>
              <div className="mt-4 text-center">
                <a
                  href={mapLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-navy hover:text-gold transition-colors font-medium text-sm"
                  data-testid="contact-map-link"
                >
                  Open in Google Maps →
                </a>
              </div>
              <div className="mt-6 p-6 bg-navy/5 rounded-lg">
                <h3 className="font-heading font-semibold text-navy mb-2">Keystone Properties</h3>
                <p className="text-slate-600 text-sm">
                  17 Commercial Street<br />
                  Batley<br />
                  WF17 5HJ
                </p>
                <a 
                  href="tel:01924 520163" 
                  className="inline-flex items-center gap-2 mt-3 text-navy font-medium hover:text-gold transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  01924 520163
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
